import { useParams } from "react-router-dom"

import { Breadcrumb, Button, Flex, Link, Tabs } from "@/atoms"
import { NAMES_ROUTES } from "@/const/routes.constants"
import * as AdrDomain from "@/domain/adr"
import { useLogger } from "@/hooks/useLogger"
import { useTranslation } from "@/hooks/useTranslation"
import { CloseCircleOutlined } from "@/icons"
import { HeaderPage } from "@/organisms/HeaderPage"
import {
  useAdrIncident,
  useDismissIncident
} from "@/pages/TriagePage/application/useAdrIncidents"
import { DismissModal } from "@/pages/TriagePage/components/molecules/DismissModal"
import { IncidentDismissInfo } from "@/pages/TriagePage/components/molecules/DismissModal/IncidentDismissInfo"
import { ShareButton } from "@/pages/TriagePage/components/molecules/ShareButton"
import { AdrSummary } from "@/pages/TriagePage/components/pages/AdrPage/components/AdrSummary"
import { DetailsTabAdr } from "@/pages/TriagePage/components/pages/AdrPage/components/DetailsTabAdr"
import { OverviewTabAdr } from "@/pages/TriagePage/components/pages/AdrPage/components/OverviewTabAdr"
import { useDismissModal } from "@/pages/TriagePage/hooks/useDismissModal"
import { PageWithBreadcrumbTemplate } from "@/templates/PageWithBreadcrumbTemplate"
import { TabsTemplate } from "@/templates/TabsTemplate"
import { EvidenceTabAdr } from "./components/EvidenceTabAdr"
import { NextStepsTabAdr } from "./components/NextStepsTabAdr"
import { VulnerableFunctionsTabAdr } from "./components/VulnerableFunctionsTabAdr"

import styles from "./AdrPage.module.scss"

export const AdrPage = (props: any) => {
  const { id = "" } = useParams()
  const { response, isLoading } = useAdrIncident(id)
  const incident = response?.data?.[0]
  const { t } = useTranslation()
  const { t: tTabs } = useTranslation("translation", {
    keyPrefix: "triage.adr.tabs"
  })
  const { ROOT, INCIDENTS } = NAMES_ROUTES.TRIAGE
  const { logger, EVENTS } = useLogger()
  const dismiss = useDismissIncident()
  const { loading, open, openModal, asyncOk, onCancel } = useDismissModal({
    onOk: (attr) => {
      if (incident) {
        const { id, type, priority } = incident
        const meta = { id, type, priority }

        logger.info(EVENTS.ANALYTIC_EVENTS.ADR.DISMISS_INCIDENT, meta)

        return dismiss?.dismissIncident?.(id, { reason: attr?.reason })
      }
    }
  })

  const showEvidence = AdrDomain.canShowEvidence(incident)

  return (
    <PageWithBreadcrumbTemplate
      loading={isLoading}
      classes={{ main: styles.main }}
      header={
        <HeaderPage
          title={
            <Breadcrumb
              items={[
                {
                  title: t("navigation.triage"),
                  className: "capitalize-text-ks"
                },
                {
                  title: (
                    <Link to={`/${ROOT}/${INCIDENTS}`}>
                      {t("navigation.incidents")}
                    </Link>
                  ),
                  className: "capitalize-text-ks"
                },
                {
                  title: t("general.incident_one"),
                  className: "capitalize-text-ks"
                }
              ]}
            />
          }
        />
      }
    >
      {!!incident && (
        <Flex vertical className={styles.container}>
          <AdrSummary incident={incident} {...props} />
          <TabsTemplate>
            <Tabs
              items={[
                {
                  key: "overview",
                  label: tTabs("overview.title"),
                  children: <OverviewTabAdr incident={incident} {...props} />
                },
                {
                  key: "details",
                  label: tTabs("details.title"),
                  children: <DetailsTabAdr incident={incident} {...props} />
                },
                {
                  key: "vulnerable-functions",
                  forceRender: true,
                  label: "Vulnerable Functions",
                  children: AdrDomain.canShowVulnerableFunctions(incident) && (
                    <VulnerableFunctionsTabAdr incident={incident} {...props} />
                  )
                },
                {
                  key: "evidence",
                  label:
                    showEvidence &&
                    tTabs(
                      `evidence.${AdrDomain.showCallStackEvidence(incident) ? "title_callStack" : "title_runtime"}`
                    ),
                  children: showEvidence && (
                    <EvidenceTabAdr incident={incident} {...props} />
                  )
                },
                {
                  key: "next-steps",
                  label: tTabs("nextSteps.title"),
                  children: <NextStepsTabAdr incident={incident} {...props} />
                }
              ].filter(({ children }) => Boolean(children))}
              tabBarExtraContent={
                <Flex gap={12}>
                  <ShareButton
                    analyticsEvent={{ metadata: { placement: "adr" } }}
                  />
                  <Button
                    className={styles.dismissIcon}
                    icon={<CloseCircleOutlined />}
                    disabled={!!incident?.dismissDetails}
                    onClick={openModal}
                  >
                    {t("general.dismiss")}
                  </Button>
                </Flex>
              }
              onChange={(activeKey) => {
                document
                  .getElementById("PageWithBreadcrumbTemplate")
                  ?.scrollTo({ top: 0, behavior: "instant" })

                logger.info(EVENTS.ANALYTIC_EVENTS.ADR.SWITCH_TABS_ADR_PAGE, {
                  tab: activeKey
                })
              }}
            />
          </TabsTemplate>
        </Flex>
      )}
      <DismissModal
        open={open}
        onOk={asyncOk}
        onCancel={onCancel}
        status="dismiss"
        okButtonProps={{ loading }}
      >
        <IncidentDismissInfo count={1} entityType="incident" />
      </DismissModal>
    </PageWithBreadcrumbTemplate>
  )
}
